import { useState, useRef, useEffect } from 'react';
import Swal from 'sweetalert2';
import type { Host } from '@/mocks/adminHosts';
import { hostStatusLabels, hostStatusColors, hostStatusDotColors } from '@/mocks/adminHosts';

interface HostCardProps {
  host: Host;
  onViewProfile: (host: Host) => void;
  onEdit: (host: Host) => void;
  onViewCommunity: (host: Host) => void;
  onViewStats: (host: Host) => void;
  onViewRevenue: (host: Host) => void;
}

export default function HostCard({ host, onViewProfile, onEdit, onViewCommunity, onViewStats, onViewRevenue }: HostCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const menuItems = [
    { icon: 'ri-user-line', label: 'Ver perfil', action: () => { setMenuOpen(false); onViewProfile(host); } },
    { icon: 'ri-edit-line', label: 'Editar', action: () => { setMenuOpen(false); onEdit(host); } },
    { icon: 'ri-group-line', label: 'Ver comunidad', action: () => { setMenuOpen(false); onViewCommunity(host); } },
    { icon: 'ri-bar-chart-2-line', label: 'Ver estadísticas', action: () => { setMenuOpen(false); onViewStats(host); } },
    { icon: 'ri-money-dollar-circle-line', label: 'Ver ingresos futuros', action: () => { setMenuOpen(false); onViewRevenue(host); } },
  ];

  const totalInteractions = host.interactions.likes + host.interactions.comments + host.interactions.shares;

  return (
    <div className="group bg-background-50 rounded-2xl border border-background-200 hover:border-background-300/70 transition-all duration-200 overflow-hidden flex flex-col">
      {/* Photo area */}
      <div className="relative aspect-[4/3] bg-background-100 overflow-hidden">
        <img
          src={host.photoUrl}
          alt={host.name}
          className="w-full h-full object-cover object-top"
        />
        {/* Status badge - top left */}
        <span className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border ${hostStatusColors[host.status]}`}>
          {host.status === 'on_air' && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>}
          {host.status === 'active' && <span className={`w-1.5 h-1.5 rounded-full ${hostStatusDotColors[host.status]}`}></span>}
          {host.status === 'inactive' && <span className={`w-1.5 h-1.5 rounded-full ${hostStatusDotColors[host.status]}`}></span>}
          {hostStatusLabels[host.status]}
        </span>

        {/* Menu button - top right */}
        <div className="absolute top-3 right-3" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="w-8 h-8 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-black/50 transition-colors"
          >
            <i className="ri-more-2-fill text-white text-sm"></i>
          </button>

          {menuOpen && (
            <div className="absolute top-full right-0 mt-1.5 w-[200px] bg-background-50 rounded-xl border border-background-200 shadow-lg py-1.5 z-20">
              {menuItems.map((item) => (
                <button
                  key={item.label}
                  onClick={item.action}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs text-foreground-600 hover:bg-background-100 hover:text-foreground-900 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className={`${item.icon} text-sm text-foreground-400`}></i>
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info area */}
      <div className="p-4 flex flex-col gap-3 flex-1">
        {/* Name + username */}
        <div>
          <h3 className="text-foreground-950 font-bold text-sm">{host.name}</h3>
          <p className="text-foreground-400 text-[11px]">{host.username}</p>
        </div>

        {/* Programs */}
        <div className="space-y-1.5">
          {host.programs.map((prog) => (
            <div key={prog.name} className="flex items-center gap-2 text-[11px]">
              <div className="w-1.5 h-1.5 rounded-full bg-secondary-400 shrink-0"></div>
              <span className="text-foreground-700 font-semibold">{prog.name}</span>
              <span className="text-foreground-400">{prog.schedule}</span>
            </div>
          ))}
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-4 pt-2 border-t border-background-200">
          <div className="flex items-center gap-1">
            <i className="ri-user-heart-line text-foreground-400 text-xs"></i>
            <span className="text-foreground-700 text-xs font-bold">{host.followers >= 1000 ? `${(host.followers / 1000).toFixed(1)}K` : host.followers}</span>
          </div>
          <div className="flex items-center gap-1">
            <i className="ri-heart-line text-foreground-400 text-xs"></i>
            <span className="text-foreground-700 text-xs font-bold">{host.interactions.likes >= 1000 ? `${(host.interactions.likes / 1000).toFixed(1)}K` : host.interactions.likes}</span>
          </div>
          <div className="flex items-center gap-1">
            <i className="ri-chat-1-line text-foreground-400 text-xs"></i>
            <span className="text-foreground-700 text-xs font-bold">{host.interactions.comments >= 1000 ? `${(host.interactions.comments / 1000).toFixed(1)}K` : host.interactions.comments}</span>
          </div>
          <div className="flex items-center gap-1">
            <i className="ri-share-forward-line text-foreground-400 text-xs"></i>
            <span className="text-foreground-700 text-xs font-bold">{host.interactions.shares >= 1000 ? `${(host.interactions.shares / 1000).toFixed(1)}K` : host.interactions.shares}</span>
          </div>
        </div>

        {/* Total interactions */}
        <div className="flex items-center justify-between pt-2 border-t border-background-200">
          <span className="text-foreground-400 text-[10px]">Total interacciones</span>
          <span className="text-foreground-700 text-xs font-bold">{totalInteractions >= 1000 ? `${(totalInteractions / 1000).toFixed(1)}K` : totalInteractions}</span>
        </div>
      </div>
    </div>
  );
}